// LAST STAND — the thinking mode.
//
// No clock and no clicking speed. Each round you are shown the strength of the
// coming assault and offered three doctrine cards; you pick one, then the round
// resolves. Assault strength climbs geometrically, so a build that merely adds
// defence loses eventually — you have to find the multiplicative pair. It is
// the deck-building shape of a run, sized to five minutes.
import * as Fx from '../core/Fx.js';
import { modeShell, readoutMarkup, resultCard } from './chrome.js';

const START_LINES = 3;
const BASE_DEFENCE = 14;

// Cards are drawn without weighting beyond an unlock round, so the interesting
// decision is which of three offered effects fits the build you already have.
const CARDS = [
  { id: 'entrench', name: 'Entrench', detail: '+10 flat defence.', from: 0, apply: (run) => { run.flat += 10; } },
  { id: 'reserves', name: 'Call Reserves', detail: '+14 flat defence, but −1 supply.', from: 0, apply: (run) => { run.flat += 14; run.supply -= 1; } },
  { id: 'drill', name: 'Drill Cycle', detail: '+25% defence multiplier.', from: 0, apply: (run) => { run.mult *= 1.25; } },
  { id: 'doctrine', name: 'New Doctrine', detail: '+40% defence multiplier, costs a line.', from: 3, apply: (run) => { run.mult *= 1.4; run.lines -= 1; } },
  { id: 'logistics', name: 'Logistics Push', detail: '+2 supply. Supply adds defence each round.', from: 0, apply: (run) => { run.supply += 2; } },
  { id: 'fortify', name: 'Fortify', detail: 'Restore one line of defence.', from: 2, apply: (run) => { run.lines = Math.min(START_LINES + 2, run.lines + 1); } },
  { id: 'intel', name: 'Deep Intel', detail: 'Next two assaults are 25% weaker.', from: 1, apply: (run) => { run.intel += 2; } },
  { id: 'attrition', name: 'Attrition Plan', detail: 'Defence grows +6% compounding every round. Stacks.', from: 2, apply: (run) => { run.growth += 0.06; } },
  { id: 'gamble', name: 'Bold Gambit', detail: 'Double your multiplier — or lose a line.', from: 3, apply: (run) => { if (Math.random() < 0.55) { run.mult *= 2; run.note = 'The gambit paid.'; } else { run.lines -= 1; run.note = 'The gambit cost a line.'; } } },
  { id: 'commons', name: 'Total Defence', detail: '+12% multiplier for every line still standing.', from: 5, apply: (run) => { run.mult *= 1 + (0.12 * Math.max(0, run.lines)); } },
];

// The assault does not merely grow — its growth rate grows, so the effective
// step is 1.25× on round two and past 1.5× by round twenty. That matters: a
// plain exponential can be beaten forever by stacking multiplier cards (drafting
// a Drill Cycle every third round is itself exponential), and playtest sims had
// careful builds running past three hundred rounds. A super-exponential wall
// outruns any fixed per-round ratio, so every run ends — the draft decides when.
function assaultStrength(round) {
  const step = round - 1;
  return Math.round(12 * (1.25 ** step) * (1.015 ** ((step * step) / 2)));
}

export const siege = {
  id: 'siege',
  name: 'Last Stand',
  kicker: 'TACTICS / NO CLOCK',
  tagline: 'Draft doctrine, hold the line, find the build that scales.',
  description: 'The assault grows a third stronger every round and flat defence cannot keep up. Draft for compounding and a good run doubles a careless one.',
  create(ctx) {
    let dom = null;
    let run = null;

    function newRun() {
      return {
        round: 0,
        lines: START_LINES,
        flat: 0,
        mult: 1,
        supply: 0,
        growth: 0,
        compound: 1,
        intel: 0,
        history: [],
        note: '',
        offer: [],
        phase: 'draft',
        over: false,
      };
    }

    function defence() {
      return Math.round((BASE_DEFENCE + run.flat + run.supply * 4) * run.mult * run.compound);
    }

    function drawOffer() {
      const eligible = CARDS.filter((card) => card.from <= run.round);
      const picked = [];
      while (picked.length < 3 && picked.length < eligible.length) {
        const card = eligible[Math.floor(Math.random() * eligible.length)];
        if (!picked.includes(card)) picked.push(card);
      }
      run.offer = picked;
    }

    function chooseCard(id) {
      if (run.phase !== 'draft' || run.over) return;
      const card = run.offer.find((entry) => entry.id === id);
      if (!card) return;
      run.note = '';
      card.apply(run);
      Fx.burst(window.innerWidth / 2, window.innerHeight / 2, { count: 14, tone: 'cyan', spread: 120 });
      resolveRound(card);
    }

    function resolveRound(card) {
      run.round += 1;
      run.compound *= 1 + run.growth;
      let strength = assaultStrength(run.round);
      if (run.intel > 0) { strength = Math.round(strength * 0.75); run.intel -= 1; }
      const held = defence();
      const survived = held >= strength;
      if (!survived) run.lines -= 1;
      run.history.unshift({ round: run.round, card: card.name, strength, held, survived, note: run.note });
      run.history = run.history.slice(0, 8);
      if (survived) {
        Fx.flash('cyan', 0.16);
        Fx.banner(`ROUND ${run.round} HELD`, `${held} vs ${strength}`, 'cyan');
      } else {
        Fx.flash('red', 0.32);
        Fx.shake(10);
        Fx.banner(`LINE BREACHED`, `${held} vs ${strength} · ${Math.max(0, run.lines)} LINES LEFT`, 'red');
      }
      if (run.lines <= 0) { finish(); return; }
      drawOffer();
      run.phase = 'draft';
      paint();
    }

    function finish() {
      run.over = true;
      const score = run.round;
      const best = ctx.recordBest(score);
      // Rounds cluster around 12–20, with a long tail for a build that clicks.
      // Scaling by score^1.9 keeps the tail worth chasing without the cap
      // flattening everything past round twenty.
      const seconds = Math.min(900, Math.round(score ** 1.9));
      const glimmer = Math.min(40, Math.max(1, Math.floor(score * 1.2)));
      ctx.commit({ seconds, glimmer, label: `Last Stand · round ${score}` });
      // A run can end two ways — overwhelmed, or bled dry by cards that cost a
      // line. Saying which avoids a result card that reads like you lost a
      // fight your own numbers say you were winning.
      const last = run.history[0];
      const overwhelmed = last && !last.survived;
      resultCard(dom.body, {
        title: overwhelmed
          ? `Overwhelmed on round ${score} — ${last.held} against ${last.strength}.`
          : `You spent your last line on doctrine at round ${score}.`,
        lines: [
          ['Rounds held', score],
          ['Defence at the end', defence()],
          ['Last assault faced', last ? last.strength : assaultStrength(score)],
          ['Defence multiplier', `×${run.mult.toFixed(2)}`],
          ['Compounding', `×${run.compound.toFixed(2)} (+${Math.round(run.growth * 100)}%/round)`],
        ],
        seconds,
        glimmer,
        best,
      });
      dom.body.querySelector('[data-role="again"]')?.addEventListener('click', startRun);
      dom.body.querySelector('[data-role="done"]')?.addEventListener('click', () => ctx.exit());
    }

    function paint() {
      if (run.over) return;
      const incoming = assaultStrength(run.round + 1);
      const projected = defence();
      const margin = projected - incoming;
      dom.readout.innerHTML = readoutMarkup([
        ['ROUND', run.round + 1],
        ['LINES', '▮'.repeat(Math.max(0, run.lines)), run.lines <= 1 ? 'stat-danger' : ''],
        ['DEFENCE', projected],
        ['INCOMING', incoming, margin < 0 ? 'stat-danger' : 'stat-hot'],
      ]);
      dom.body.innerHTML = `<div class="siege-stage">
        <div class="siege-forecast ${margin < 0 ? 'forecast-bad' : 'forecast-good'}">
          <span class="eyebrow">ROUND ${run.round + 1} FORECAST</span>
          <div class="forecast-numbers">
            <div><small>YOUR DEFENCE</small><b>${projected}</b></div>
            <div class="forecast-versus">VS</div>
            <div><small>ASSAULT STRENGTH</small><b>${incoming}</b></div>
          </div>
          <p>${margin >= 0 ? `Holding with ${margin} to spare. The next assault is ${assaultStrength(run.round + 2)}.` : `Short by ${Math.abs(margin)}. Draft something that closes the gap or lose a line.`}</p>
        </div>
        <div class="siege-cards">${run.offer.map((card) => `<button class="siege-card" data-siege-card="${card.id}">
          <span class="card-corner">${card.name.slice(0, 2).toUpperCase()}</span>
          <strong>${card.name}</strong>
          <small>${card.detail}</small>
        </button>`).join('')}</div>
        <div class="siege-state">
          <span>FLAT +${run.flat}</span><span>MULT ×${run.mult.toFixed(2)}</span><span>SUPPLY ${run.supply}</span><span>GROWTH +${Math.round(run.growth * 100)}%/rd</span>${run.intel ? `<span class="stat-hot">INTEL ${run.intel}</span>` : ''}
        </div>
        <div class="siege-log">${run.history.map((entry) => `<div class="${entry.survived ? 'log-held' : 'log-breach'}"><b>R${entry.round}</b><span>${entry.card}</span><em>${entry.held} vs ${entry.strength}</em><i>${entry.survived ? 'HELD' : 'BREACH'}</i></div>`).join('') || '<div class="log-empty">No rounds resolved yet.</div>'}</div>
      </div>`;
    }

    function onClick(event) {
      const card = event.target.closest('[data-siege-card]');
      if (card) chooseCard(card.dataset.siegeCard);
    }

    function startRun() {
      run = newRun();
      drawOffer();
      dom.body.addEventListener('click', onClick);
      paint();
    }

    return {
      mount(root) {
        dom = modeShell(root, siege);
        dom.exit.addEventListener('click', () => ctx.exit());
        startRun();
      },
      destroy() {
        dom?.body.removeEventListener('click', onClick);
      },
    };
  },
};
